import React, { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Heading, Undo, Redo, Type } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

/** Lightweight WYSIWYG editor using contenteditable + document.execCommand.
 *  Good enough for editing contract text inline without external deps. */
export default function RichTextEditor({ value, onChange, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = sanitizeHtml(value);
    }
  }, [value]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleInput = (e) => onChange(e.currentTarget.innerHTML);

  const Btn = ({ cmd, arg, label, children, title }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      title={title || label}
      className="px-2 py-1.5 text-neutral-700 hover:bg-[#FF5A00]/10 hover:text-[#FF5A00] border border-transparent text-xs font-mono-kpi inline-flex items-center gap-1"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-neutral-300 bg-white" data-testid={testid}>
      <div className="flex items-center flex-wrap gap-0.5 border-b border-neutral-200 bg-neutral-50 px-2 py-1">
        <Btn cmd="bold" title="Negrita (Ctrl+B)"><Bold className="w-3.5 h-3.5" /></Btn>
        <Btn cmd="italic" title="Cursiva (Ctrl+I)"><Italic className="w-3.5 h-3.5" /></Btn>
        <Btn cmd="underline" title="Subrayado (Ctrl+U)"><Underline className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-4 bg-neutral-300 mx-1" />
        <Btn cmd="formatBlock" arg="H3" title="Encabezado"><Heading className="w-3.5 h-3.5" /></Btn>
        <Btn cmd="formatBlock" arg="P" title="Párrafo"><Type className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-4 bg-neutral-300 mx-1" />
        <Btn cmd="insertUnorderedList" title="Lista"><List className="w-3.5 h-3.5" /></Btn>
        <Btn cmd="insertOrderedList" title="Lista numerada"><ListOrdered className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-4 bg-neutral-300 mx-1" />
        <Btn cmd="undo" title="Deshacer"><Undo className="w-3.5 h-3.5" /></Btn>
        <Btn cmd="redo" title="Rehacer"><Redo className="w-3.5 h-3.5" /></Btn>
        <div className="ml-auto text-[10px] font-mono-kpi text-neutral-500 uppercase tracking-wider pr-2">Editor visual</div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-testid={testid ? `${testid}-area` : undefined}
        className="prose-aa max-w-none px-6 py-4 min-h-[420px] outline-none focus:bg-neutral-50/30"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: "13px", lineHeight: 1.55 }}
      />
    </div>
  );
}
